import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  HelpCircle,
  BarChart,
} from "lucide-react";
import {
  dashboardRoutePath,
  platformManagementRoutePath,
  settingsRoutePath,
  usageManagementRoutePath,
} from "../../containers/CoreRouter";
import { useUserSession } from "../../contexts/UserSessionContext";
import config from "../../config";
import { getFirstLetter } from "../../utils/Strings";

const MainLayout = ({ children, handleLogOut }: any) => {
  const { userSession } = useUserSession();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleNavbar = () => setIsOpen(!isOpen);
  const closeNavbar = () => setIsOpen(false);

  // Close drawer when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const navItems = [
    { label: "Dashboard", link: dashboardRoutePath, icon: <LayoutDashboard size={18} />, external: false },
    { label: "Platforms", link: platformManagementRoutePath, icon: <Layers size={18} />, external: false },
    { label: "Usage", link: usageManagementRoutePath, icon: <BarChart size={18} />, external: false },
    { label: "Billing", link: config.links.stripeCustomerPortalURL, icon: <CreditCard size={18} />, external: true },
    { label: "Settings", link: settingsRoutePath, icon: <Settings size={18} />, external: false },
  ];

  const isActive = (path: string) => location.pathname === path;

  const renderNavItem = (item: any, index: any) => {
    const linkClasses = `apple-nav-item ${isActive(item.link) ? "apple-nav-item-active" : ""}`;
    if (item.external) {
      return (
        <a
          key={index}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClasses}
          onClick={closeNavbar}
        >
          <span className="mr-3">{item.icon}</span>
          <span className="font-semibold">{item.label}</span>
          <ExternalLink size={14} className="ml-auto opacity-70" />
        </a>
      );
    }
    return (
      <a key={index} href={item.link} className={linkClasses} onClick={closeNavbar}>
        <span className="mr-3">{item.icon}</span>
        <span className="font-semibold">{item.label}</span>
      </a>
    );
  };

  return (
    <div className="min-h-screen flex flex-col paper">
      {/* Top Header sticky, mobile-friendly */}
      <header className="glass-navbar py-3 px-3 sm:px-4 lg:px-6 flex items-center justify-between sticky top-0 z-40 animate-fade-in">
        <div className="flex items-center min-w-0">
          <button
            onClick={toggleNavbar}
            className="glass-button p-2 mr-2 sm:mr-3 rounded-xl transition-colors duration-200 lg:hidden"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <a
            href={dashboardRoutePath}
            className="flex items-center text-lg sm:text-xl font-bold text-ink text-display min-w-0 truncate"
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo.webp`}
              alt="AICosts.ai Logo"
              className="w-7 h-7 sm:w-8 sm:h-8 mr-2 flex-shrink-0"
            />
            <span className="truncate">AICosts.ai</span>
          </a>
        </div>

        {/* User avatar on mobile + tablet (logout lives in drawer) */}
        <div className="flex items-center gap-2 lg:hidden">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold shadow-apple text-sm"
            style={{ background: "linear-gradient(135deg, #1d1d1f 0%, #000 100%)" }}
            title={userSession?.name}
          >
            {getFirstLetter(userSession?.name)}
          </div>
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Overlay for mobile drawer */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 lg:hidden backdrop-blur-sm transition-opacity duration-300"
            onClick={closeNavbar}
            aria-hidden
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 lg:top-14 left-0 h-screen lg:h-[calc(100vh-56px)] z-40 lg:z-auto
                      w-[86vw] max-w-[320px] lg:w-64 xl:w-72 flex-shrink-0
                      transition-transform duration-300 ease-in-out
                      bg-white/95 lg:bg-transparent
                      backdrop-blur-xl lg:backdrop-blur-none
                      border-r border-black/[0.07] lg:border-r-0
                      shadow-2xl lg:shadow-none
                      ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
                      overflow-y-auto`}
          aria-label="Primary navigation"
        >
          <div className="flex flex-col h-full">
            {/* Mobile-only header inside drawer */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-black/[0.06] lg:hidden">
              <span className="flex items-center font-bold text-ink text-display">
                <img
                  src={`${process.env.PUBLIC_URL}/logo.webp`}
                  alt=""
                  className="w-7 h-7 mr-2"
                  aria-hidden
                />
                AICosts.ai
              </span>
              <button
                onClick={closeNavbar}
                className="p-2 rounded-xl text-ink-soft hover:bg-black/5"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            {/* Nav items */}
            <nav className="flex-1 py-4 lg:py-6 overflow-y-auto">
              <ul className="space-y-1 lg:space-y-2 px-3 lg:px-2">
                {navItems.map((item, index) => (
                  <li key={index}>{renderNavItem(item, index)}</li>
                ))}
              </ul>

              {/* Support card */}
              <div className="px-3 lg:px-2 mt-6">
                <div className="glass-card p-4 lg:p-5 rounded-2xl">
                  <h4 className="text-sm font-bold text-ink mb-1.5">Need help?</h4>
                  <p className="text-xs text-ink-soft mb-3 leading-snug">
                    Contact our support team for any questions.
                  </p>
                  <a
                    href={`mailto:${config.emails.support}`}
                    className="apple-button-primary text-xs flex items-center justify-center py-2.5 w-full"
                  >
                    <HelpCircle size={14} className="mr-2" />
                    Contact Support
                  </a>
                </div>
              </div>
            </nav>

            {/* User footer visible on ALL screens */}
            <div className="p-3 lg:p-4 border-t border-black/[0.06] flex-shrink-0">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold shadow-apple text-sm flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #1d1d1f 0%, #000 100%)" }}
                >
                  {getFirstLetter(userSession?.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink text-sm truncate">{userSession?.name}</p>
                  <p className="text-xs text-ink-soft truncate">{userSession?.email}</p>
                </div>
                <button
                  onClick={handleLogOut}
                  className="p-2 text-ink-soft hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Log Out"
                  aria-label="Log out"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-w-0 relative overflow-x-hidden">
          <div className="animate-fade-in">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
